import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Alert, AlertDescription } from "../components/ui/alert";
import { Separator } from "../components/ui/separator";
import { 
  BarChart3, 
  Package, 
  ShoppingCart, 
  Users, 
  DollarSign,
  TrendingUp,
  Clock,
  AlertCircle,
  LogOut,
  Settings,
  Gem,
  Calendar,
  Database,
  Download
} from "lucide-react";

const AdminDashboardPage = () => {
  const [stats, setStats] = useState(null);
  const [adminInfo, setAdminInfo] = useState(null);
  const [recentOrders, setRecentOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [backupLoading, setBackupLoading] = useState(false);
  const navigate = useNavigate();

  const getAuthHeaders = () => {
    const token = localStorage.getItem("adminToken");
    if (!token) {
      navigate("/admin/login");
      return null;
    }
    return {
      "Authorization": `Bearer ${token}`,
      "Content-Type": "application/json"
    };
  };

  const fetchDashboardData = async () => {
    try {
      const headers = getAuthHeaders();
      if (!headers) return;

      // Fetch admin info
      const adminResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/me`, {
        headers
      });
      
      if (adminResponse.ok) {
        const adminData = await adminResponse.json();
        setAdminInfo(adminData);
      }

      // Fetch order stats
      const statsResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/orders/stats`, {
        headers
      });
      
      if (statsResponse.ok) {
        const statsData = await statsResponse.json();
        setStats(statsData);
      }

      // Admin info already fetched above

    } catch (err) {
      setError("Failed to load dashboard data");
      console.error("Dashboard error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("adminToken");
    localStorage.removeItem("adminTokenExpires");
    navigate("/admin/login");
  };

  const handleDatabaseBackup = async () => {
    try {
      setBackupLoading(true);
      const headers = getAuthHeaders();
      if (!headers) {
        alert("⚠️ Authentication required. Please login again.");
        return;
      }

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/database/export`, {
        headers
      });

      if (response.status === 403) {
        alert("🔒 Access Denied: Super admin role required for database export");
        return;
      }

      if (!response.ok) {
        const errorText = await response.text();
        let errorMsg = "Failed to export database";
        try {
          const errorJson = JSON.parse(errorText);
          errorMsg = errorJson.detail || errorMsg;
        } catch (e) {
          errorMsg = errorText || errorMsg;
        }
        throw new Error(errorMsg);
      }

      // Get the blob
      const blob = await response.blob();
      
      // Check if we got data
      if (blob.size === 0) {
        throw new Error("Export returned empty file");
      }
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `database_backup_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      
      // Cleanup
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      // Show success with file size
      const sizeMB = (blob.size / 1024 / 1024).toFixed(2);
      alert(`✅ Database backup downloaded successfully!\n\nFile size: ${sizeMB} MB\nFilename: database_backup_${new Date().toISOString().split('T')[0]}.json`);
    } catch (err) {
      console.error("Backup error:", err);
      alert(`❌ Failed to backup database:\n\n${err.message}\n\nPlease check:\n• You are logged in as super admin\n• Your internet connection is stable\n• Try refreshing the page and logging in again`);
    } finally {
      setBackupLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    const statusStyles = {
      pending: "bg-yellow-100 text-yellow-800 border-yellow-200",
      confirmed: "bg-blue-100 text-blue-800 border-blue-200",
      processing: "bg-purple-100 text-purple-800 border-purple-200",
      shipped: "bg-green-100 text-green-800 border-green-200",
      delivered: "bg-emerald-100 text-emerald-800 border-emerald-200",
      cancelled: "bg-red-100 text-red-800 border-red-200"
    };

    return (
      <Badge className={statusStyles[status] || "bg-gray-100 text-gray-800"}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mb-4"></div>
          <p className="text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="bg-purple-600 w-8 h-8 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center">
                <Gem className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              </div>
              <div>
                <h1 className="text-lg sm:text-2xl font-bold text-gray-900">Admin Dashboard</h1>
                <p className="text-xs sm:text-sm text-gray-500 hidden sm:block">Umoona Opal Mine & Museum</p>
              </div>
            </div>
            
            <div className="flex items-center gap-2 sm:gap-4">
              {adminInfo && (
                <div className="text-right hidden md:block">
                  <p className="text-sm font-medium text-gray-900">{adminInfo.name}</p>
                  <p className="text-xs text-gray-500 capitalize">{adminInfo.role.replace('_', ' ')}</p>
                </div>
              )}
              <Button variant="outline" size="sm" onClick={handleLogout}>
                <LogOut className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Logout</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        
        {error && (
          <Alert className="mb-6 border-red-200 bg-red-50">
            <AlertCircle className="h-4 w-4 text-red-600" />
            <AlertDescription className="text-red-600">{error}</AlertDescription>
          </Alert>
        )}

        {/* Quick Actions */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Quick Actions
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 sm:p-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                {/* Manage Orders */}
                <Button 
                  className="h-auto p-4 sm:p-6 justify-center text-center flex-col space-y-2 sm:space-y-3 hover:shadow-lg transition-all duration-300" 
                  variant="outline"
                  onClick={() => navigate("/admin/orders")}
                >
                  <div className="bg-blue-100 p-3 rounded-lg">
                    <ShoppingCart className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">Manage Orders</p>
                    <p className="text-xs text-gray-500 mt-1">View and update order status</p>
                  </div>
                </Button>

                {/* Manage Products */}
                <Button 
                  className="h-auto p-4 sm:p-6 justify-center text-center flex-col space-y-2 sm:space-y-3 hover:shadow-lg transition-all duration-300" 
                  variant="outline"
                  onClick={() => navigate("/admin/products")}
                >
                  <div className="bg-purple-100 p-3 rounded-lg">
                    <Package className="w-6 h-6 text-purple-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">Manage Products</p>
                    <p className="text-xs text-gray-500 mt-1">Add, edit, and organize products</p>
                  </div>
                </Button>

                {/* Manage Categories */}
                <Button 
                  className="h-auto p-4 sm:p-6 justify-center text-center flex-col space-y-2 sm:space-y-3 hover:shadow-lg transition-all duration-300" 
                  variant="outline"
                  onClick={() => navigate("/admin/categories")}
                >
                  <div className="bg-amber-100 p-3 rounded-lg">
                    <Gem className="w-6 h-6 text-amber-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">Manage Categories</p>
                    <p className="text-xs text-gray-500 mt-1">Product categories</p>
                  </div>
                </Button>

                {/* Tour Bookings */}
                <Button 
                  className="h-auto p-4 sm:p-6 justify-center text-center flex-col space-y-2 sm:space-y-3 hover:shadow-lg transition-all duration-300" 
                  variant="outline"
                  onClick={() => navigate("/admin/bookings")}
                >
                  <div className="bg-orange-100 p-3 rounded-lg">
                    <Calendar className="w-6 h-6 text-orange-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">Tour Bookings</p>
                    <p className="text-xs text-gray-500 mt-1">Underground mine tours</p>
                  </div>
                </Button>

                {/* Live Dashboard */}
                <Button 
                  className="h-auto p-4 sm:p-6 justify-center text-center flex-col space-y-2 sm:space-y-3 hover:shadow-lg transition-all duration-300" 
                  variant="outline"
                  onClick={() => navigate("/admin/live-dashboard")}
                >
                  <div className="bg-teal-100 p-3 rounded-lg">
                    <TrendingUp className="w-6 h-6 text-teal-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">Live Dashboard</p>
                    <p className="text-xs text-gray-500 mt-1">Real-time booking data</p>
                  </div>
                </Button>

                {/* Admin Users */}
                <Button 
                  className="h-auto p-4 sm:p-6 justify-center text-center flex-col space-y-2 sm:space-y-3 hover:shadow-lg transition-all duration-300" 
                  variant="outline"
                  onClick={() => navigate("/admin/users")}
                >
                  <div className="bg-indigo-100 p-3 rounded-lg">
                    <Users className="w-6 h-6 text-indigo-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">Admin Users</p>
                    <p className="text-xs text-gray-500 mt-1">Manage admin accounts</p>
                  </div>
                </Button>

                {/* Settings */}
                <Button 
                  className="h-auto p-4 sm:p-6 justify-center text-center flex-col space-y-2 sm:space-y-3 hover:shadow-lg transition-all duration-300" 
                  variant="outline"
                  onClick={() => navigate("/admin/settings")}
                >
                  <div className="bg-gray-100 p-3 rounded-lg">
                    <Settings className="w-6 h-6 text-gray-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">System Settings</p>
                    <p className="text-xs text-gray-500 mt-1">Email, shipping, features</p>
                  </div>
                </Button>

                {/* Blackout Dates */}
                <Button 
                  className="h-auto p-4 sm:p-6 justify-center text-center flex-col space-y-2 sm:space-y-3 hover:shadow-lg transition-all duration-300" 
                  variant="outline"
                  onClick={() => navigate("/admin/blackout-dates")}
                  data-testid="blackout-dates-btn"
                >
                  <div className="bg-red-100 p-3 rounded-lg">
                    <AlertCircle className="w-6 h-6 text-red-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">Blackout Dates</p>
                    <p className="text-xs text-gray-500 mt-1">Manage tour availability</p>
                  </div>
                </Button>

                {/* Analytics & Reporting */}
                <Button 
                  className="h-auto p-4 sm:p-6 justify-center text-center flex-col space-y-2 sm:space-y-3 hover:shadow-lg transition-all duration-300" 
                  variant="outline"
                  onClick={() => navigate("/admin/analytics")}
                >
                  <div className="bg-green-100 p-3 rounded-lg">
                    <BarChart3 className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">Analytics & Reports</p>
                    <p className="text-xs text-gray-500 mt-1">View insights and data</p>
                  </div>
                </Button>

                {/* Full Database Backup */}
                <Button 
                  className="h-auto p-4 sm:p-6 justify-center text-center flex-col space-y-2 sm:space-y-3 hover:shadow-lg transition-all duration-300 border-2 border-blue-200 bg-blue-50" 
                  variant="outline"
                  onClick={handleDatabaseBackup}
                  disabled={backupLoading}
                >
                  <div className="bg-blue-100 p-3 rounded-lg">
                    {backupLoading ? (
                      <Download className="w-6 h-6 text-blue-600 animate-bounce" />
                    ) : (
                      <Database className="w-6 h-6 text-blue-600" />
                    )}
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">Full Database Backup</p>
                    <p className="text-xs text-gray-500 mt-1">
                      {backupLoading ? "Exporting..." : "Export all data as JSON"}
                    </p>
                  </div>
                </Button>
              </div>
            </CardContent>
          </Card>
      </div>
    </div>
  );
};

export default AdminDashboardPage;